import React from "react";

export default function LandingPage() {
  const [form, setForm] = React.useState({ name: "", contact: "", message: "" });
  const [sending, setSending] = React.useState(false);

  // v3 site key: zet jouw echte site-key als REACT_APP_RECAPTCHA_SITE_KEY_V3 in je build env.
  const RECAPTCHA_SITE_KEY =
    process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3 || "6LdYqfcrAAAAAB8NmC507l5CSWsbXNUCvEJ8BAxP";

  React.useEffect(() => {
    // laad reCAPTCHA v3 script
    if (!document.querySelector(`script[src="https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}"]`)) {
      const s = document.createElement("script");
      s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
  }, [RECAPTCHA_SITE_KEY]);

  function onChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);
    try {
      if (!window.grecaptcha) throw new Error("reCAPTCHA not loaded");

      // wacht tot grecaptcha klaar is (ready verwacht een callback)
      await new Promise((resolve) => {
        try {
          window.grecaptcha.ready(resolve);
        } catch (err) {
          // fallback: als ready throws, resolve direct zodat execute later faalt duidelijk
          console.error("grecaptcha.ready error", err);
          resolve();
        }
      });

      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "submit" });

      const payload = { ...form, recaptchaToken: token };

      const res = await fetch("/api/sendMail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errBody = "";
        try {
          errBody = await res.text();
        } catch {}
        throw new Error(`Send failed: ${res.status} ${errBody}`);
      }

      alert("Bericht verzonden — we nemen snel contact op.");
      setForm({ name: "", contact: "", message: "" });
    } catch (err) {
      console.error(err);
      alert("Er is iets misgegaan bij het verzenden.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="min-h-screen text-slate-50 antialiased"
      style={{
        backgroundImage: "url('/Background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#070726",
      }}
    >
      {/* NAV */}
      <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between bg-slate-900/70 rounded-xl shadow-lg backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <img src="/Drijvers-Tech Solutions_logo.png" alt="Drijvers-Tech Solutions logo" className="h-12 w-auto object-contain drop-shadow-lg" />
          <div>
            <h1 className="text-3xl font-extrabold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-300 to-blue-400 drop-shadow-lg">
              Drijvers‑Tech Solutions
            </h1>
            <p className="text-blue-100 max-w-xl drop-shadow-sm">waar technologie en gemak samen komt</p>
          </div>
        </div>

        <nav className="hidden md:flex gap-6 items-center text-blue-100">
          <a href="#services" className="hover:underline hover:text-white">
            Diensten
          </a>
          <a href="#features" className="hover:underline hover:text-white">
            Waarom wij
          </a>
          <a href="#contact" className="hover:underline hover:text-white">
            Contact
          </a>
          <a href="#quote" className="ml-2 inline-flex items-center gap-2 rounded-2xl px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold shadow-lg">
            Vraag offerte aan
          </a>
        </nav>

        <button className="md:hidden text-blue-200/90">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
      </header>

      {/* HERO */}
      <main className="max-w-6xl mx-auto px-6 py-12 md:py-20">
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-200 to-blue-400 drop-shadow-lg">
              Slimme technologie voor elk huis en bedrijf
            </h2>
            <p className="mt-4 text-blue-100 max-w-xl drop-shadow-sm">
              Wij ontwerpen, installeren en onderhouden gebruiksvriendelijke slimme oplossingen — van domotica tot netwerkbeheer — zodat technologie echt werkt voor jou.
            </p>
            <div className="mt-8 flex gap-4">
              <a href="#contact" className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold shadow-lg">
                Plan een afspraak
              </a>
              <a href="#services" className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 border border-blue-600 text-blue-100 hover:border-white hover:text-white transition">
                Onze diensten
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-blue-100">
              <div className="bg-slate-800/80 px-3 py-2 rounded-lg">Installatie & Integratie</div>
              <div className="bg-slate-800/80 px-3 py-2 rounded-lg">24/7 Support</div>
              <div className="bg-slate-800/80 px-3 py-2 rounded-lg">Op maat gemaakte oplossingen</div>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="w-full max-w-md p-8 bg-gradient-to-b from-slate-900/90 to-slate-900/75 rounded-2xl shadow-2xl border border-blue-800/60">
              <img src="/Drijvers-Tech Solutions.png" alt="Logo" className="w-full h-auto object-contain" />
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-800/85 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-200">Smart Home</div>
                  <div className="text-xs text-blue-100">Automatisering & beveiliging</div>
                </div>
                <div className="p-3 bg-slate-800/85 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-200">Netwerk</div>
                  <div className="text-xs text-blue-100">Snel & betrouwbaar</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="mt-16">
          <h3 className="text-2xl font-semibold text-blue-100">Onze diensten</h3>
          <p className="mt-2 text-blue-200 max-w-2xl">Complete oplossingen voor particulieren en bedrijven — van advies tot onderhoud.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Domotica Installatie",
                desc: "Verlichtingsscènes, klimaatregeling en beveiliging die samenwerken.",
              },
              { title: "Automatisering", desc: "Bedrijfsprocessen te optimaliseren en automatiseren voor meer efficiëntie en groei." },
              { title: "Netwerk & Wifi", desc: "Ontwerp en optimalisatie voor stabiele verbindingen." },
              { title: "Monitoring & Support", desc: "Proactieve monitoring en snelle hulp wanneer nodig." },
              { title: "IoT Integratie", desc: "Apparaten laten communiceren met slimme flows." },
              { title: "Energiebeheer", desc: "Slimme besparing en inzicht in verbruik." },
            ].map((s) => (
              <article key={s.title} className="p-6 rounded-2xl bg-gradient-to-br from-slate-900/85 to-blue-900/40 border border-blue-800/60 shadow-lg">
                <h4 className="font-semibold text-lg text-blue-100">{s.title}</h4>
                <p className="mt-2 text-sm text-blue-200">{s.desc}</p>
              </article>
            ))}
          </div>
        </section>
        {/* FEATURES / WHY US */}
        <section id="features" className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 p-6 rounded-2xl bg-slate-900/85 border border-blue-800/60 shadow-lg">
            <h3 className="text-2xl font-semibold text-blue-100">Waarom kiezen voor Drijvers‑Tech Solutions?</h3>
            <ul className="mt-4 space-y-4 text-blue-200">
              <li>• Design-first aanpak: we bouwen systemen die ook prettig werken voor mensen.</li>
              <li>• Transparante prijzen en duidelijke planning.</li>
              <li>• Support en onderhoud, zodat jouw systeem jaren probleemloos draait.</li>
            </ul>
          </div>
          {/* <div className="p-6 rounded-2xl bg-gradient-to-r from-blue-400/10 to-cyan-400/10 border border-blue-800/60 shadow-lg">
            <h4 className="font-semibold text-blue-100">Snelle factcheck</h4>
            <div className="mt-4 space-y-3 text-sm text-blue-200">
              <div className="flex justify-between">
                <span>Installaties per jaar</span>
                <span className="font-bold">+120</span>
              </div>
              <div className="flex justify-between">
                <span>Tevreden klanten</span>
                <span className="font-bold">98%</span>
              </div>
              <div className="flex justify-between">
                <span>Response support</span>
                <span className="font-bold">1 uur</span>
              </div>
            </div>
          </div> */}
        </section>
        {/* CONTACT / CTA */}
        <section id="contact" className="mt-16 p-8 rounded-2xl bg-slate-900/85 border border-blue-800/60 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="text-2xl font-semibold text-blue-100">Start je project</h3>
              <p className="mt-2 text-blue-200">Vertel ons kort wat je zoekt en we nemen snel contact op.</p>
              <ul className="mt-4 text-sm text-blue-200 space-y-2">
                <li>✉️ Email: <span className="text-blue-50 font-medium">info@drijverstechsolutions.nl</span></li>
                <li>📍 Locatie: <span className="text-blue-50 font-medium">Oosterhout NB, NL</span></li>
              </ul>
            </div>

            <form id="quote" className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input name="name" value={form.name} onChange={onChange} className="p-3 rounded-lg bg-slate-800/80 border border-blue-700 text-blue-900 placeholder-blue-300" placeholder="Naam" required />
                <input name="contact" value={form.contact} onChange={onChange} className="p-3 rounded-lg bg-slate-800/80 border border-blue-700 text-blue-900 placeholder-blue-300" placeholder="Telefoon of e-mail" required />
              </div>

              <textarea name="message" value={form.message} onChange={onChange} className="w-full p-3 rounded-lg bg-slate-800/80 border border-blue-700 text-blue-900 placeholder-blue-300" rows={4} placeholder="Kort project/vragen" required />

              <div className="flex items-center gap-3">
                <button type="submit" disabled={sending} className="px-4 py-2 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold">
                  {sending ? "Bezig..." : "Verstuur"}
                </button>
                <button type="button" onClick={() => setForm({ name: "", contact: "", message: "" })} className="px-4 py-2 rounded-2xl border border-blue-700 text-blue-100">
                  Annuleer
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="mt-16 text-sm text-blue-200 text-center pb-12 drop-shadow-sm">
          © {new Date().getFullYear()} Drijvers‑Tech Solutions — Alle rechten voorbehouden
        </footer>
      </main>
    </div>
  );
}